import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { PaymentSessionResponseDto } from '@/lib/models/paymentSessionResponseDto';
import { PayComContext } from './context';
import {
  DEFAULT_CHECKOUT_STATE,
  detectWalletsNatively,
  handleExistingSources,
  handlePaymentMethods,
  initializePayDotComInstance,
} from './service';
import type {
  InferredPayComCanMakePaymentsType,
  PayComCheckoutState,
  SavedBankInstrument,
  SavedCardInstrument,
} from './types';

/**
 * Returns the Pay.com session info for SDK init. Deposit passes a payment
 * session (`/deposit/:id/session`); withdraw passes a setup session
 * (`/withdrawals/setup-session`). Either yields the same `{ sessionId,
 * clientSecret, merchantId, expiresAt }` shape — the SDK doesn't care which
 * mode it was minted in until a payment / tokenize call is invoked.
 */
export type PayComSessionFetcher = () => Promise<PaymentSessionResponseDto>;

interface PayComProviderProps {
  sessionFetcher: PayComSessionFetcher;
  /**
   * Stable identity for the session source. When this changes, the provider
   * re-mints. Pass the deposit id (deposit flow) or a fixed string like
   * `'withdraw-setup'` (withdraw flow).
   */
  sessionKey: string;
  /**
   * Persists removal of a saved card server-side (deposit passes the
   * `DELETE /deposit/payment-methods/:id` call, which revokes the Pay.com vault
   * entry). When omitted, removal is local-only — the withdraw flow has no
   * delete affordance, so it never wires this.
   */
  onDeleteSavedCard?: (savedCardId: string) => Promise<void>;
  children: ReactNode;
}

export function PayComProvider({ sessionFetcher, sessionKey, onDeleteSavedCard, children }: PayComProviderProps) {
  const [state, setState] = useState<PayComCheckoutState>(DEFAULT_CHECKOUT_STATE);
  // Always-fresh handle on the saved cards so an optimistic delete can snapshot
  // the exact list synchronously (for rollback) without racing a pending
  // setState updater.
  const savedCardsRef = useRef<SavedCardInstrument[]>(state.savedCards);
  savedCardsRef.current = state.savedCards;

  useEffect(() => {
    if (!sessionKey) return;

    let cancelled = false;
    let walletTimeoutId: number | null = null;
    let savedCardsTimeoutId: number | null = null;
    let walletListenerCleanup: (() => void) | null = null;
    setState({ ...DEFAULT_CHECKOUT_STATE });

    (async () => {
      let session: PaymentSessionResponseDto;
      try {
        session = await sessionFetcher();
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          isFailed: true,
          isInitializing: false,
          error: err instanceof Error ? err.message : 'Could not start a Pay.com session.',
        }));
        return;
      }
      if (cancelled) return;

      let checkout;
      try {
        const namespace = await initializePayDotComInstance(session.merchantId);
        if (cancelled) return;
        checkout = namespace.checkout({ clientSecret: session.clientSecret });
      } catch (err) {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          isFailed: true,
          isInitializing: false,
          error: err instanceof Error ? err.message : 'Could not load the Pay.com SDK.',
        }));
        return;
      }

      // Surface readiness as soon as the SDK is mounted — saved cards,
      // wallet availability, and accepted methods fill in progressively.
      // If we waited on `getPaymentMethods` / `canMakePayments`, a slow or
      // hung SDK iframe would freeze the entire deposit form.
      setState((prev) => ({
        ...prev,
        checkoutObject: checkout,
        sessionId: session.sessionId,
        sessionExpiresAt: new Date(session.expiresAt),
        isInitializing: false,
        isReady: true,
        isFailed: false,
        error: null,
      }));

      checkout
        .getPaymentMethods()
        .then((methods) => {
          if (cancelled) return;
          const { allowedPaymentMethods, allowedCardBrands } = handlePaymentMethods(methods);
          setState((prev) => ({ ...prev, allowedPaymentMethods, allowedCardBrands }));
        })
        .catch((err) => console.warn('Pay.com getPaymentMethods failed:', err));

      // `canMakePayments()` can resolve `false` before the SDK iframe finishes
      // its handshake (HEADLESS_READY event), causing Apple Pay to flash off on
      // first render. Wait for HEADLESS_READY; fall back to native detection
      // only if the event never fires.
      const HEADLESS_READY_TIMEOUT_MS = 4_000;
      let walletsResolved = false;

      const finalizeWallets = (result: InferredPayComCanMakePaymentsType) => {
        if (cancelled || walletsResolved) return;
        walletsResolved = true;
        setState((prev) => ({
          ...prev,
          googlepay: result.googlepay === true,
          applepay: result.applepay === true,
          walletsDetected: true,
        }));
      };

      const askSdkForWallets = () => {
        (checkout.canMakePayments() as Promise<InferredPayComCanMakePaymentsType>)
          .then((result) => finalizeWallets(result ?? {}))
          .catch((err) => {
            console.warn('Pay.com canMakePayments failed; using native detection:', err);
            finalizeWallets(detectWalletsNatively());
          });
      };

      const headlessReadyEvent = checkout.EVENT_TYPES.HEADLESS_READY;
      checkout.once(headlessReadyEvent, askSdkForWallets);
      walletListenerCleanup = () => checkout.removeListener(headlessReadyEvent, askSdkForWallets);

      walletTimeoutId = window.setTimeout(() => {
        if (walletsResolved || cancelled) return;
        finalizeWallets(detectWalletsNatively());
      }, HEADLESS_READY_TIMEOUT_MS);

      // `getExistingSources` can hang on a slow iframe handshake. Cap at the
      // same 4s budget as the wallet detection so the deposit UI is never
      // stuck on a skeleton — we'd rather show the new-card form a hair
      // late than not at all.
      const SAVED_CARDS_TIMEOUT_MS = 4_000;
      const markSavedSourcesLoaded = (cards?: SavedCardInstrument[], bankAccounts?: SavedBankInstrument[]) => {
        if (cancelled) return;
        setState((prev) => {
          if (prev.savedCardsLoaded && cards === undefined && bankAccounts === undefined) return prev;
          return {
            ...prev,
            savedCards: cards ?? prev.savedCards,
            savedBankAccounts: bankAccounts ?? prev.savedBankAccounts,
            savedCardsLoaded: true,
          };
        });
      };

      checkout
        .getExistingSources()
        .then((sources) => {
          const { cards, bankAccounts } = handleExistingSources(sources);
          markSavedSourcesLoaded(cards, bankAccounts);
        })
        .catch((err) => {
          console.warn('Pay.com getExistingSources failed:', err);
          markSavedSourcesLoaded([], []);
        });

      savedCardsTimeoutId = window.setTimeout(() => markSavedSourcesLoaded(), SAVED_CARDS_TIMEOUT_MS);
    })();

    return () => {
      cancelled = true;
      if (walletTimeoutId !== null) window.clearTimeout(walletTimeoutId);
      if (savedCardsTimeoutId !== null) window.clearTimeout(savedCardsTimeoutId);
      walletListenerCleanup?.();
    };
  }, [sessionKey, sessionFetcher]);

  const deleteSavedCard = useCallback(
    async (savedCardId: string) => {
      // Snapshot the current list up front so a failed server call can restore
      // it verbatim, then optimistically drop the card.
      const previous = savedCardsRef.current;
      setState((prev) => ({ ...prev, savedCards: prev.savedCards.filter((c) => c.id !== savedCardId) }));

      // No persister wired (withdraw has no delete affordance) → local-only.
      if (!onDeleteSavedCard) return;

      try {
        await onDeleteSavedCard(savedCardId);
      } catch (err) {
        // Server rejected the removal — put the list back exactly as it was.
        setState((prev) => ({ ...prev, savedCards: previous }));
        throw err;
      }
    },
    [onDeleteSavedCard],
  );

  return <PayComContext.Provider value={{ state, deleteSavedCard }}>{children}</PayComContext.Provider>;
}
